/**
 * The sheets of the Season Automations pages: which room to show, a new automation's start, rename, delete, and what
 * needs attention.
 */
import { useId, useState } from "react";
import type { AutomationDetail, AutomationSummary } from "../../shared/types";
import { GroupedList } from "../components/season/GroupedList";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Sheet } from "../components/ui/Sheet";
import { STARTS, type Start } from "./starts";
import { attentionText } from "./seasonParts";

/** The rooms to show, each with how many automations run on it. */
export function ShowSheet({ open, onClose, list, value, onPick }: { open: boolean; onClose: () => void; list: AutomationSummary[]; value: string; onPick: (room: string) => void }) {
  const rooms = new Map<string, string>();
  for (const a of list) for (const r of a.rooms) rooms.set(r.webinar_id, r.title);
  const options: Array<[string, string, number]> = [
    ["all", "All", list.length],
    ...[...rooms].map(([id, title]): [string, string, number] => [id, title, list.filter((a) => a.rooms.some((r) => r.webinar_id === id)).length]),
  ];
  return (
    <Sheet open={open} onClose={onClose} title="Show">
      <GroupedList noLead>
        {options.map(([id, title, n]) => (
          <li key={id} className="k-lr">
            <button
              type="button"
              className="k-lr-b"
              onClick={() => {
                onPick(id);
                onClose();
              }}
            >
              <span className="k-lr-t">
                <b>{title}</b>
              </span>
              <span className="k-lr-v">{n}</span>
              {value === id ? <Icon name="check" /> : <span className="au-nochk" />}
            </button>
          </li>
        ))}
      </GroupedList>
    </Sheet>
  );
}

/** New automation: the ready-made starts, made at once and opened. */
export function NewSheet({ open, onClose, onContinue, busy }: { open: boolean; onClose: () => void; onContinue: (start: Start) => void; busy: boolean }) {
  const [pick, setPick] = useState<Start["key"]>(STARTS[0].key);
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="New automation"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="p"
            after="right"
            disabled={busy}
            onClick={() => onContinue(STARTS.find((s) => s.key === pick) ?? STARTS[0])}
          >
            Continue
          </Button>
        </>
      }
    >
      <GroupedList noLead className="au-opts">
        {STARTS.map((s) => (
          <li key={s.key} className="k-lr">
            <button type="button" role="radio" aria-checked={pick === s.key} className="k-lr-b au-opt" onClick={() => setPick(s.key)}>
              <span className="au-rd" />
              <span className="k-lr-t">
                <b>{s.title}</b>
                {s.line ? <span>{s.line}</span> : null}
              </span>
            </button>
          </li>
        ))}
      </GroupedList>
    </Sheet>
  );
}

export function RenameSheet({ open, onClose, name, onSave }: { open: boolean; onClose: () => void; name: string; onSave: (name: string) => void }) {
  const id = useId();
  const [text, setText] = useState(name);
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Rename"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="p"
            disabled={!text.trim()}
            onClick={() => {
              onSave(text);
              onClose();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="field">
        <label htmlFor={id}>Name</label>
        <input dir="auto" id={id} className="inp" value={text} autoComplete="off" onChange={(e) => setText(e.target.value)} />
      </div>
    </Sheet>
  );
}

export function DeleteSheet({ open, onClose, name, onDelete }: { open: boolean; onClose: () => void; name: string; onDelete: () => void }) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Delete this automation?"
      sub="Anyone in it stops where they are."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" onClick={onDelete}>
            Delete
          </Button>
        </>
      }
    >
      <p className="au-aw">{name}</p>
    </Sheet>
  );
}

/** What stopped, with the two things to do about it. */
export function AttentionSheet({ open, onClose, d, onSettle }: { open: boolean; onClose: () => void; d: AutomationDetail; onSettle: (how: "retry" | "stop") => void }) {
  const said = attentionText(d);
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Needs attention"
      footer={
        <>
          <Button
            onClick={() => {
              onClose();
              onSettle("stop");
            }}
          >
            Stop them
          </Button>
          <Button
            variant="p"
            onClick={() => {
              onClose();
              onSettle("retry");
            }}
          >
            Try again
          </Button>
        </>
      }
    >
      {said ? (
        <p className="au-aw">
          <b>{said.words}.</b> {said.reason}
        </p>
      ) : null}
    </Sheet>
  );
}
